import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';
import { formatUtcDate } from '@/helpers/formatUtcDate';
import { DEFAULT_LOCALE } from '@/locales/localeConfig';
import type { PromptExecutionHistoryResponse } from '../../types';
import { PANEL_FONT_FAMILY } from '../../utils/panel-font';
import { ChartLegend, useLegendHidden } from '../dashboard/competition/ChartLegend';

const SERIES_COLORS = ['#059669', '#7c3aed', '#2563eb', '#d97706', '#db2777', '#0891b2'];

export type PromptHistoryTrendMetric = 'position' | 'visibility' | 'shareOfVoice';

function tehranDayKey(iso: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DEFAULT_LOCALE.timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

function averageValues(
  values: Array<number | null | undefined>,
  skipNonPositive: boolean,
): number | null {
  const nums = values.filter(
    (value): value is number => value != null && (!skipNonPositive || value > 0),
  );
  if (!nums.length) return null;
  return Math.round((nums.reduce((sum, value) => sum + value, 0) / nums.length) * 10) / 10;
}

function mentionWeight(position: number | null): number {
  if (position == null || !Number.isFinite(position) || position <= 0) return 0.7;
  return 0.7 + 0.3 * Math.max(0, 1 - (Math.max(1, position) - 1) / 10);
}

function visibilityFromPosition(position: number | null): number {
  if (position == null || position <= 0) return 0;
  return Math.round(100 * mentionWeight(position));
}

function shareAmongTracked(
  brands: PromptExecutionHistoryResponse['brands'],
  index: number,
  brand: PromptExecutionHistoryResponse['brands'][number],
): number {
  const own = brand.positions[index];
  if (own == null || own <= 0) return 0;
  const allWeight = brands.reduce((sum, item) => {
    const position = item.positions[index];
    return sum + (position != null && position > 0 ? mentionWeight(position) : 0);
  }, 0);
  if (allWeight <= 0) return 0;
  return Math.round((mentionWeight(own) / allWeight) * 1000) / 10;
}

function brandValues(
  history: PromptExecutionHistoryResponse,
  brand: PromptExecutionHistoryResponse['brands'][number],
  metric: PromptHistoryTrendMetric,
): Array<number | null> {
  if (metric === 'position') return brand.positions;
  if (metric === 'visibility') {
    return brand.visibility?.length
      ? brand.visibility
      : brand.positions.map(visibilityFromPosition);
  }
  // shortcut: SOV among tracked brands when API omits shareOfVoice, drop after backend ships series
  return brand.shareOfVoice?.length
    ? brand.shareOfVoice
    : brand.positions.map((_, index) => shareAmongTracked(history.brands, index, brand));
}

function dailyMetricSeries(history: PromptExecutionHistoryResponse, metric: PromptHistoryTrendMetric) {
  const groups: { iso: string; indexes: number[] }[] = [];
  const indexByDay = new Map<string, number>();

  history.dates.forEach((iso, index) => {
    const key = tehranDayKey(iso);
    const existing = indexByDay.get(key);
    if (existing == null) {
      indexByDay.set(key, groups.length);
      groups.push({ iso, indexes: [index] });
      return;
    }
    groups[existing].indexes.push(index);
  });

  const skipNonPositive = metric === 'position';

  return {
    labels: groups.map(group => formatUtcDate(group.iso, { preset: 'date' }) || tehranDayKey(group.iso)),
    brands: history.brands.map(brand => {
      const values = brandValues(history, brand, metric);
      return {
        ...brand,
        values: groups.map(group =>
          averageValues(
            group.indexes.map(index => values[index]),
            skipNonPositive,
          ),
        ),
      };
    }),
  };
}

const EMPTY_MESSAGE: Record<PromptHistoryTrendMetric, string> = {
  position: 'حداقل دو روز با اجرای موفق لازم است تا نمودار رتبه نمایش داده شود.',
  visibility: 'در این بازه داده روزانه برای دیده‌شدن نیست.',
  shareOfVoice: 'در این بازه داده روزانه برای سهم از دیده‌شدن نیست.',
};

export function PromptPositionTrendChart({
  history,
  metric,
}: {
  history: PromptExecutionHistoryResponse | null;
  metric: PromptHistoryTrendMetric;
}) {
  const {
    config: { isDark },
  } = useAppContext() as { config: { isDark: boolean } };
  const { hidden, toggle } = useLegendHidden();

  const daily = useMemo(() => {
    if (!history?.brands.length) return null;
    const series = dailyMetricSeries(history, metric);
    if (metric === 'position' && series.labels.length < 2) return null;
    if (!series.labels.length) return null;
    return series;
  }, [history, metric]);

  const option = useMemo(() => {
    if (!daily) return null;

    const visibleBrands = daily.brands.filter(brand => !hidden.has(brand.name));
    if (!visibleBrands.length) return null;

    const isRank = metric === 'position';
    const plotted = visibleBrands.flatMap(brand =>
      brand.values.filter((value): value is number => value != null),
    );
    if (!plotted.length) return null;

    const maxPosition = Math.max(5, ...plotted);
    const axisLabelColor = getColor('gray-700');
    const axisLineColor = getColor('gray-300');
    const splitLineColor = getColor('gray-200');
    const mutedText = getColor('gray-600');
    const tooltipBg = getColor('gray-100');
    const tooltipBorder = getColor('gray-300');
    const tooltipText = getColor('gray-1100');
    const valueSuffix = isRank ? '' : '%';
    const valuePrefix = isRank ? '#' : '';

    return {
      color: visibleBrands.map(
        brand => SERIES_COLORS[daily.brands.indexOf(brand) % SERIES_COLORS.length],
      ),
      tooltip: {
        trigger: 'axis',
        backgroundColor: tooltipBg,
        borderColor: tooltipBorder,
        borderWidth: 1,
        extraCssText: 'direction: rtl; text-align: right;',
        textStyle: { color: tooltipText, fontSize: 12, fontFamily: PANEL_FONT_FAMILY },
        formatter: (
          params: Array<{
            seriesName: string;
            value: number | null;
            axisValue: string;
            color: string;
          }>,
        ) => {
          if (!params.length) return '';
          const rows = params
            .filter(item => item.value != null)
            .map(
              item => `
              <div style="margin-top:6px">
                <span style="display:inline-block;width:8px;height:8px;border-radius:999px;background:${item.color};margin-inline-end:6px"></span>
                <strong>${item.seriesName}</strong>
                <span style="color:${mutedText};padding-inline-start:14px">${isRank ? 'رتبه' : metric === 'visibility' ? 'دیده‌شدن' : 'سهم از دیده‌شدن'} ${valuePrefix}${item.value}${valueSuffix}</span>
              </div>`,
            )
            .join('');
          return `<div style="font-family:${PANEL_FONT_FAMILY};font-size:12px;direction:rtl;text-align:right;color:${tooltipText}"><div style="font-weight:600;margin-bottom:2px">${params[0].axisValue}</div>${rows}</div>`;
        },
      },
      legend: { show: false },
      grid: { left: 16, right: 36, top: 12, bottom: 28 },
      xAxis: {
        type: 'category',
        data: daily.labels,
        boundaryGap: daily.labels.length <= 14,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisLabel: { color: axisLabelColor, fontSize: 11, fontFamily: PANEL_FONT_FAMILY },
        axisTick: { show: false },
      },
      yAxis: isRank
        ? {
            type: 'value',
            inverse: true,
            min: 1,
            max: Math.ceil(maxPosition),
            interval: 1,
            splitLine: { lineStyle: { color: splitLineColor } },
            axisLabel: {
              color: axisLabelColor,
              fontSize: 11,
              fontFamily: PANEL_FONT_FAMILY,
              formatter: '#{value}',
            },
          }
        : {
            type: 'value',
            min: 0,
            max: 100,
            splitLine: { lineStyle: { color: splitLineColor } },
            axisLabel: {
              color: axisLabelColor,
              fontSize: 11,
              fontFamily: PANEL_FONT_FAMILY,
              formatter: '{value}%',
            },
          },
      series: visibleBrands.map(brand => ({
        name: brand.name,
        type: 'line',
        smooth: true,
        showSymbol: daily.labels.length <= 14,
        symbolSize: 6,
        lineStyle: { width: brand.relationship === 'own_brand' ? 3 : 2 },
        data: brand.values,
        connectNulls: false,
      })),
    };
  }, [daily, hidden, isDark, metric]);

  if (!history?.brands.length || !daily) {
    return (
      <div
        className="d-flex align-items-center justify-content-center rounded-3 border border-dashed bg-body-tertiary px-4 text-center fs--1 text-500"
        style={{ height: 220 }}
      >
        {EMPTY_MESSAGE[metric]}
      </div>
    );
  }

  return (
    <div>
      {option ? (
        <ReactECharts
          key={`${isDark ? 'dark' : 'light'}-${metric}`}
          option={option}
          notMerge
          style={{ height: 220 }}
          opts={{ renderer: 'svg' }}
        />
      ) : (
        <div
          className="d-flex align-items-center justify-content-center rounded-3 border border-dashed bg-body-tertiary px-4 text-center fs--1 text-500"
          style={{ height: 220 }}
        >
          برندی برای نمایش انتخاب نشده.
        </div>
      )}
      <ChartLegend
        onToggle={toggle}
        items={daily.brands.map((brand, index) => ({
          id: brand.name,
          label: brand.name,
          color: SERIES_COLORS[index % SERIES_COLORS.length],
          bold: brand.relationship === 'own_brand',
          hidden: hidden.has(brand.name),
        }))}
      />
    </div>
  );
}
